import { Box, capitalize } from '@mui/material';
import { GridColDef, type GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { StyleObj } from '../../../../@types';
import { PaymentList } from '../../../../@types/api';
import { generateDateRangeTimestamps, getTotalsRowClass } from '../../../../helpers';
import { createDescriptionColumn, createNumberColumn } from '../../../../helpers/columns';
import { usePagination } from '../../../../hooks/usePagination';
import useSort from '../../../../hooks/useSort';
import { createColumn } from '../../../../utils';
import { formatPaymentMethod } from '../../../../helpers/paymentDisplay';
import API_ROUTES from '../../../../constants/apiRoutes';
import ZeroXWithdrawalDetails, { WITHDRAWAL_STATE_LABELS } from '../../../molecules/ZeroXWithdrawalDetails';
import DateRangeSelect, { DayjsTimestamp } from '../../../molecules/DateRangeSelect';
import TableTemplate from '../../../templates/TableTemplate';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const DEFAULT_VISIBLE_COLUMNS: GridColumnVisibilityModel = {
  id: true,
  playerFullName: true,
  agentFullName: true,
  executionTime: true,
  type: true,
  paymentMethod: true,
  cryptoProvider: true,
  cryptoAsset: true,
  grossCryptoAmount: true,
  cryptoWithdrawal: true,
  withdrawalState: true,
  cryptoProviderTransactionId: false,
  cryptoAccountDebit: false,
  cryptoAccountCurrency: false,
  cryptoEstimatedFee: false,
  cryptoEstimatedReceived: false,
  cryptoActualFee: false,
  cryptoFee: false,
  cryptoDestination: false,
  cryptoHash: false,
  cryptoFailureReason: false,
  amount: true,
  status: true,
  description: true,
};

const columns: GridColDef[] = [
  createColumn('id', 'ID', {
    sortable: false,
  }),
  createColumn('playerFullName', 'Player', {
    sortable: false,
    minWidth: 150,
  }),
  createColumn('agentFullName', 'Updated by', {
    sortable: false,
    minWidth: 150,
  }),
  createColumn('executionTime', 'Date & Time', {
    valueGetter: (value, row, column, apiRef) => dayjs(value).format('DD-MM-YYYY HH:mm:ss'),
  }),
  createColumn('type', 'Type', {
    valueFormatter: (value, row, column, apiRef) => capitalize(value),
  }),
  createColumn('paymentMethod', 'Pay Method', {
    valueFormatter: (value, row: PaymentList) => formatPaymentMethod(value, row.metadata),
  }),
  createColumn('cryptoProvider', 'Crypto provider', {
    sortable: false,
    minWidth: 150,
    valueGetter: (_value, row: PaymentList) => {
      const provider = row.metadata?.cryptoWithdrawal?.provider ?? row.metadata?.crypto?.provider;
      return provider === 'zero_x_processing' ? '0xProcessing' : provider === 'moralis' ? 'Moralis' : '—';
    },
  }),
  createColumn('cryptoAsset', 'Crypto asset', {
    sortable: false,
    minWidth: 150,
    valueGetter: (_value, row: PaymentList) =>
      row.metadata?.cryptoWithdrawal?.assetCurrency ?? row.metadata?.crypto?.assetCurrency ?? '—',
  }),
  createColumn('grossCryptoAmount', 'Gross crypto amount', {
    sortable: false,
    minWidth: 180,
    valueGetter: (_value, row: PaymentList) =>
      row.metadata?.cryptoWithdrawal?.assetAmount ?? row.metadata?.crypto?.grossAssetAmount ?? '—',
  }),
  createColumn('cryptoWithdrawal', 'Crypto withdrawal', {
    sortable: false,
    disableExport: true,
    minWidth: 180,
    renderCell: (params) => (
      <ZeroXWithdrawalDetails withdrawal={(params.row as PaymentList).metadata?.cryptoWithdrawal} />
    ),
  }),
  createColumn('withdrawalState', 'Withdrawal state', {
    sortable: false,
    minWidth: 250,
    valueGetter: (_value, row: PaymentList) =>
      row.metadata?.cryptoWithdrawal ? WITHDRAWAL_STATE_LABELS[row.metadata.cryptoWithdrawal.state] : '—',
  }),
  // Scalar, exportable withdrawal facts (the dialog above is render-only).
  ...(
    [
      [
        'cryptoProviderTransactionId',
        'Provider transaction ID',
        (row: PaymentList) => row.metadata?.cryptoWithdrawal?.providerWithdrawalId,
      ],
      ['cryptoAccountDebit', 'Account debit', (row: PaymentList) => row.metadata?.cryptoWithdrawal?.debit],
      [
        'cryptoAccountCurrency',
        'Account currency',
        (row: PaymentList) => row.metadata?.cryptoWithdrawal?.accountCurrency,
      ],
      [
        'cryptoEstimatedFee',
        'Estimated network fee',
        (row: PaymentList) => row.metadata?.cryptoWithdrawal?.estimatedNetworkFee,
      ],
      [
        'cryptoEstimatedReceived',
        'Estimated crypto received',
        (row: PaymentList) => row.metadata?.cryptoWithdrawal?.estimatedReceived,
      ],
      ['cryptoActualFee', 'Actual network fee', (row: PaymentList) => row.metadata?.cryptoWithdrawal?.actualFee],
      [
        'cryptoFee',
        'Network fee',
        (row: PaymentList) => {
          const withdrawal = row.metadata?.cryptoWithdrawal;
          if (!withdrawal) return undefined;
          return withdrawal.actualFee != null
            ? `${withdrawal.actualFee} ${withdrawal.assetCurrency}`
            : `${withdrawal.estimatedNetworkFee} ${withdrawal.assetCurrency} (estimated)`;
        },
      ],
      [
        'cryptoDestination',
        'Destination',
        (row: PaymentList) => {
          const withdrawal = row.metadata?.cryptoWithdrawal;
          return withdrawal && (withdrawal.tag ? `${withdrawal.address} (tag ${withdrawal.tag})` : withdrawal.address);
        },
      ],
      ['cryptoHash', 'Transaction hash', (row: PaymentList) => row.metadata?.cryptoWithdrawal?.hash],
      ['cryptoFailureReason', 'Failure reason', (row: PaymentList) => row.metadata?.cryptoWithdrawal?.failureReason],
    ] as const
  ).map(([field, header, value]) =>
    createColumn(field, header, {
      sortable: false,
      minWidth: 180,
      valueGetter: (_value, row: PaymentList) => value(row) ?? '—',
    })
  ),
  createNumberColumn('amount', 'Amount'),
  createColumn('status', 'Status', {
    valueFormatter: (value, row, column, apiRef) => capitalize(value),
  }),
  createDescriptionColumn(),
];

const getMobileCardHeaderItems = (payment: PaymentList) => [
  { value: payment.playerFullName, flex: 1, align: 'left' as const },
  { label: capitalize(payment.type), value: payment.amount.toFixed(2) },
  { label: 'Status', value: capitalize(payment.status) },
];

const PaymentsListReportsPage = () => {
  const { fromTimestamp, toTimestamp } = generateDateRangeTimestamps('today');
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<PaymentList>(
    API_ROUTES.REPORTS.PAYMENTS.LIST,
    {
      page: 1,
      limit: 25,
      fromTimestamp: fromTimestamp.valueOf(),
      toTimestamp: toTimestamp.valueOf(),
    }
  );
  const { handleSort } = useSort(changeQuery);

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  return (
    <Box sx={styles.container}>
      <TableTemplate
        getRowId={(params) => params.id}
        rows={data?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        showExport
        exportAllColumns
      />
    </Box>
  );
};

export default PaymentsListReportsPage;
